import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FreeShippingProgressBar } from '@vp/om-free-shipping-progress-bar';
import { getAuthHeaderAndShopperId } from '@vp/common-api-utils/authHelpers';
import type { RootState, ShippingOption } from '../types';
import { shippingOptionSelectors } from '../state/shippingOptions';
import SiteContext from './contexts/SiteContext';
import { addressesSelectors } from '../state/addresses';
import { cartSelectors } from '../state/cart';
import { getBaseShippingPrice } from '../util/shippingOptionsHelper';

// note: this is separated out from the main component to avoid unnecessary re-renders of the
//  client provided options approach when lastModifiedDate changes
const TenantOptionsWrapper = React.memo(() => {
  const {
    uiLocalization: { locale },
  } = useContext(SiteContext);

  const lastModifiedDate = useSelector((state: RootState) =>
    cartSelectors.selectLastModifiedDate(state.cart)
  );
  const shippingAddressCountry = useSelector((state: RootState) =>
    addressesSelectors.selectShippingAddressCountry(state.addresses)
  );
  const tenant = useSelector((state: RootState) =>
    cartSelectors.selectTenant(state.cart)
  );
  const shopperId = useSelector((state: RootState) =>
    cartSelectors.selectShopperId(state.cart)
  );

  return (
    <FreeShippingProgressBar
      locale={locale}
      auth={{
        shopperId,
        authHeader: getAuthHeaderAndShopperId().authHeader,
      }}
      tenant={tenant}
      lastModifiedDate={lastModifiedDate}
      destinationCountry={shippingAddressCountry || ''}
      requestor="cartui"
    />
  );
});
TenantOptionsWrapper.displayName = 'TenantOptionsWrapper';

export const FreeShippingProgressBarWrapper = React.memo(() => {
  const {
    uiLocalization: { locale },
  } = useContext(SiteContext);

  const shippingQuotesRetrievalStatus = useSelector((state: RootState) =>
    shippingOptionSelectors.selectRetrievalStatus(state.shippingOptions)
  );

  const shippingQuotes = useSelector((state: RootState) =>
    shippingOptionSelectors.selectShippingOptionsToDisplay(
      state.shippingOptions
    )
  );

  const restructuredShippingQuotes = useMemo(
    () =>
      shippingQuotes
        .filter((quote: ShippingOption) => !!quote.pricingData)
        .map((quote: ShippingOption) => ({
          displayText: quote.displayText,
          pricingData: {
            currency: quote.pricingData!.currency,
            fractionDigits: quote.pricingData!.fractionDigits,
            netListCents: getBaseShippingPrice(quote).netListCents,
            freeShippingDetails: quote.pricingData!.freeShippingDetails,
          },
        })),
    [shippingQuotes]
  );

  // if we have successfully retrieved shipping quotes use that data
  if (
    shippingQuotesRetrievalStatus !== undefined &&
    shippingQuotesRetrievalStatus === 'SUCCESS'
  ) {
    return (
      <FreeShippingProgressBar
        locale={locale}
        shippingQuotes={restructuredShippingQuotes}
      />
    );
  }

  // otherwise just use tenant options
  return <TenantOptionsWrapper />;
});
FreeShippingProgressBarWrapper.displayName = 'FreeShippingProgressBarWrapper';
